import React from "react";
import { FileWarning, ExternalLink, Landmark } from "lucide-react";
import { Reveal, ExternalLinkIcon, SectionLabel } from "./primitives";
import { businessDetails } from "../data/content";
import { track } from "../lib/analytics";

export const ComplaintsSection = () => (
  <section id="complaints" aria-labelledby="complaints-heading" className="bg-brand-bg py-16 lg:py-24">
    <div className="mx-auto max-w-content px-4 lg:px-8">
      <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
        <Reveal className="rounded-2xl border border-brand-border bg-white p-7 lg:p-9">
          <SectionLabel>Complaints</SectionLabel>
          <h2
            id="complaints-heading"
            className="mt-3 font-heading text-3xl font-bold tracking-tight text-brand-navy lg:text-4xl"
          >
            Making a complaint
          </h2>
          <div className="mt-4 space-y-4 text-lg leading-relaxed text-brand-body">
            <p>
              We aim to provide a high standard of service. If you are unhappy with any aspect
              of the service you have received, please tell us so that we can review your
              concerns.
            </p>
            <p>
              You should first follow the Freeman Jones complaints policy, which explains how to
              make a complaint, the information to provide and how your complaint will be
              handled.
            </p>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a
              href={businessDetails.complaintsPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("complaints_policy_clicked", { location: "complaints" })}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-brand-teal px-5 text-center font-semibold text-white transition-colors hover:bg-brand-tealHover"
              data-testid="complaints-read-policy"
              aria-label="Read our Complaints Policy (PDF) — opens in a new tab"
            >
              <FileWarning className="h-5 w-5" aria-hidden="true" /> Read our Complaints Policy
              <ExternalLinkIcon />
            </a>
            <a
              href="#contact"
              className="inline-flex min-h-[48px] items-center justify-center rounded-full border border-brand-navy px-5 font-semibold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
              data-testid="complaints-contact"
            >
              Contact us about a complaint
            </a>
          </div>
          <p className="mt-4 text-sm text-brand-body">
            The Freeman Jones complaints policy is the primary complaints route.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="rounded-2xl border border-brand-border bg-brand-navy p-7 text-white lg:p-9">
          <h3 className="font-heading text-xl font-bold text-white">
            If you remain dissatisfied
          </h3>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-white/80">
            <p>
              You can also refer a complaint concerning an Insolvency Practitioner to the
              Insolvency Practitioner Complaints Gateway at any time, or if you remain
              dissatisfied following our response.
            </p>
            <p>
              The online complaints form is available through GOV.UK. Alternatively, the
              Insolvency Service Enquiry Line can provide guidance on completing the complaints
              process.
            </p>
            <p>Customers in Northern Ireland should contact the appropriate regulatory body.</p>
          </div>
          <div className="mt-6 space-y-3">
            <a
              href={businessDetails.complaintsGatewayUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("external_regulator_link_clicked", { label: "gov-uk-gateway" })}
              className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-white px-5 font-semibold text-brand-navy transition-colors hover:bg-white/90"
              data-testid="complaints-gateway"
              aria-label="Complain about an insolvency practitioner on GOV.UK — opens in a new tab"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" /> GOV.UK Complaints Gateway
              <ExternalLinkIcon />
            </a>
            <p className="text-base text-white/80">
              Insolvency Service Enquiry Line:{" "}
              <a href={businessDetails.insolvencyServicePhoneLink} className="font-semibold text-white underline">
                {businessDetails.insolvencyServicePhone}
              </a>
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const RegulatorySection = () => (
  <section
    id="regulatory"
    aria-labelledby="regulatory-heading"
    className="border-t border-brand-border bg-white py-14 lg:py-20"
  >
    <div className="mx-auto max-w-content px-4 lg:px-8">
      <Reveal className="rounded-2xl border border-brand-border bg-brand-bg p-7 lg:p-9">
        <div className="flex items-start gap-4">
          <Landmark className="mt-1 h-7 w-7 flex-shrink-0 text-brand-teal" aria-hidden="true" />
          <div>
            <SectionLabel>Regulatory information</SectionLabel>
            <h2
              id="regulatory-heading"
              className="mt-3 font-heading text-2xl font-bold tracking-tight text-brand-navy lg:text-3xl"
            >
              Authorisation and regulation
            </h2>
            <div className="mt-4 space-y-4 text-base leading-relaxed text-brand-body">
              <p>
                Freeman Jones Limited has Licensed Insolvency Practitioners who are authorised
                by the Insolvency Practitioners Association.
              </p>
              <p>
                Catherine McNeill &amp; Michael Bellingham are licenced Insolvency
                Practitioners regulated by The Insolvency Practitioners Association.
              </p>
            </div>

            <div className="mt-6 rounded-xl border border-brand-border bg-white p-5">
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-teal">
                Insolvency Practitioners Association
              </p>
              <address className="mt-2 not-italic text-base leading-relaxed text-brand-navy">
                Head of Regulation<br />
                46 New Broad Street<br />
                London<br />
                EC2M 1JH
              </address>
              <a
                href="https://insolvency-practitioners.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("external_regulator_link_clicked", { label: "ipa-website" })}
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-brand-teal underline-offset-2 hover:underline"
                data-testid="ipa-website-link"
                aria-label="Visit the Insolvency Practitioners Association website — opens in a new tab"
              >
                insolvency-practitioners.org.uk <ExternalLinkIcon className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
